import { useCallback, useRef } from 'react';

const LONG_PRESS_MS = 450;
const MOVE_TOLERANCE_PX = 10;

/**
 * Touch long-press detector for list rows — the mobile entry into bulk
 * selection (round 4: the standing bulk-edit toggle button is gone).
 *
 * Fires `onLongPress` after a still 450ms hold; any meaningful finger travel
 * (scroll or a SwipeableRow drag) cancels it. After firing, the synthetic
 * click that follows touchend is suppressed so the row doesn't also open.
 */
export function useLongPress(onLongPress: () => void, { disabled = false } = {}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  const onTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (disabled || event.touches.length !== 1) {
        return;
      }
      const touch = event.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
      firedRef.current = false;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        firedRef.current = true;
        navigator.vibrate?.(10);
        onLongPress();
      }, LONG_PRESS_MS);
    },
    [disabled, onLongPress]
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!startRef.current || !timerRef.current) {
        return;
      }
      const touch = event.touches[0];
      if (
        Math.abs(touch.clientX - startRef.current.x) > MOVE_TOLERANCE_PX ||
        Math.abs(touch.clientY - startRef.current.y) > MOVE_TOLERANCE_PX
      ) {
        clear();
      }
    },
    [clear]
  );

  const onTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      if (firedRef.current) {
        // Swallow the click that would follow this touch — the long-press
        // already consumed the gesture.
        event.preventDefault();
        firedRef.current = false;
      }
      clear();
    },
    [clear]
  );

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd };
}
